/**
 * Wireframe overlay — the mesh's structure, seen (ADR-031 T0).
 *
 * The zones arc begins with a person being able to READ the mesh: where the
 * triangles are, how dense, where a tube narrows, where a coat panel is a sheet
 * two triangles thick. Allen's first requirement for segmenting a character was
 * "a way to superimpose geometry — show the wireframe of the model"; every zone
 * proposal after this (the tail tint, the wand's live selection) is judged
 * against these lines.
 *
 * WHAT IT DRAWS. A GHOST SkinnedMesh over each of the subject's skinned
 * meshes, wearing an unlit wireframe material: the SAME geometry object (no
 * copy — a wireframe adds no attribute, so unlike the weight overlay there is
 * nothing of our own to hang on a private geometry), the same skeleton, the
 * same morph state, so it rides the playing clip for free and disposing it
 * restores the world exactly. The pattern is the weight overlay's (ADR-027),
 * and the ghost is stamped through the SAME identity (`markViewportDebris`), so
 * exportGlb leaves it out of user files and soleSkinnedMesh never counts it as
 * a second subject — every door that ignores one ghost ignores this one.
 *
 * DEPTH TEST OFF, DELIBERATELY. A depth-tested line drawn on its own surface
 * stipples: the line's interpolated depth and the triangle's differ by an ulp
 * across most pixels. The classic cure, a polygon offset, is a TRIANGLE
 * rasterizer feature — under WebGPU `depthBias` does not apply to line
 * primitives — and the other cure (offsetting the SUBJECT's material) would
 * touch what the user ships. So the wire is an x-ray at low opacity: every
 * edge, front and back, like Blender's wireframe shading. Opacity is the dial;
 * the field decides whether hidden lines help or clutter.
 *
 * FAIL-LOUD (§7): `show` returns null on success or a REFUSAL STRING ("no
 * subject loaded", "subject has no skinned mesh") for the caller to surface;
 * it never leaves a blank standing for an answer.
 *
 * View-layer only — it draws what the geometry says and decides nothing.
 * Depends on: three, adapter-three (the ghost identity). Depended on by: main
 * (wires it to the `4` key — Maya's wireframe key; Shift+4 steps faint → mid → hard).
 */
import { MeshBasicMaterial, Object3D, SkinnedMesh } from 'three';
import { isViewportDebris, markViewportDebris } from '@harryhausen/adapter-three';

export interface WireframeOverlay {
  /** Draw the wire over the current subject. Null on success, else a refusal to surface. */
  show(): string | null;
  /** 0.1..1 — an x-ray wire reads best low; 1.0 is a hard cage. */
  setOpacity(opacity: number): void;
  hide(): void;
  dispose(): void;
  /** Live evidence for probes and the status line. */
  probe(): { on: boolean; opacity: number; ghosts: number };
}

// The field's first read at 0.35: "legible but heavy-handed … wants to be a
// little more subtle" (Allen, 2026-09-02, the tiger). Faint by default; the
// dial climbs to mid and hard from here.
const DEFAULT_OPACITY = 0.2;
const MIN_OPACITY = 0.1;
/** A cool, light line that reads over both fur and dark leather; unlit, so it is the same everywhere. */
const WIRE_COLOR = 0x8fd8ff;

function skinnedMeshes(root: Object3D): SkinnedMesh[] {
  const found: SkinnedMesh[] = [];
  root.traverse((o) => {
    if ((o as SkinnedMesh).isSkinnedMesh && !isViewportDebris(o)) found.push(o as SkinnedMesh);
  });
  return found;
}

export function createWireframeOverlay(getScene: () => Object3D | null): WireframeOverlay {
  interface Ghost {
    readonly source: SkinnedMesh;
    readonly mesh: SkinnedMesh;
    readonly material: MeshBasicMaterial;
  }
  let ghosts: Ghost[] = [];
  let on = false;
  let opacity = DEFAULT_OPACITY;

  function teardown(): void {
    for (const g of ghosts) {
      g.mesh.removeFromParent();
      g.material.dispose();
      // The geometry is the SUBJECT's, shared by reference — never disposed here.
    }
    ghosts = [];
  }

  /** Build (or rebuild) one ghost per skinned mesh of the CURRENT subject. */
  function ensureGhosts(): string | null {
    const scene = getScene();
    // A refusal releases whatever ghosts an EARLIER subject built — holding
    // the old model's meshes behind a hidden overlay is a leak the GC cannot
    // argue with (the weight overlay's Codex P2, #98 round four, worn here).
    if (!scene) {
      teardown();
      return 'no subject loaded';
    }
    const sources = skinnedMeshes(scene);
    if (sources.length === 0) {
      teardown();
      return 'subject has no skinned mesh';
    }
    // A subject swap leaves ghosts bound to a dead skeleton — rebuild when the
    // sources changed, not just when the count did.
    const stale = ghosts.length !== sources.length || ghosts.some((g, i) => g.source !== sources[i]);
    if (stale) {
      teardown();
      for (const source of sources) {
        const material = new MeshBasicMaterial({
          wireframe: true,
          color: WIRE_COLOR,
          transparent: true,
          opacity,
          depthTest: false, // see the module header — an x-ray wire, by decision
          depthWrite: false,
        });
        // The SOURCE's geometry object, shared: a wire adds no attribute.
        const ghost = new SkinnedMesh(source.geometry, material);
        // No scene-graph name (name-based animation binding must never find
        // it); identity is the session mark, and the ONE mark every door reads.
        markViewportDebris(ghost, 'wireframe');
        ghost.renderOrder = (source.renderOrder ?? 0) + 2; // above the heat ghost, which sits at +1
        ghost.skeleton = source.skeleton;
        ghost.bindMode = source.bindMode;
        ghost.bind(source.skeleton, source.bindMatrix);
        ghost.frustumCulled = false; // the source's culling volume answers for both
        // Morph STATE shared by array reference, so the wire morphs with the flesh.
        if (source.morphTargetInfluences) ghost.morphTargetInfluences = source.morphTargetInfluences;
        if (source.morphTargetDictionary) ghost.morphTargetDictionary = source.morphTargetDictionary;
        // A CHILD of the source at identity: it inherits the source's live
        // transform and effective visibility every frame (ADR-027's reasoning).
        source.add(ghost);
        ghosts.push({ source, mesh: ghost, material });
      }
    }
    return null;
  }

  return {
    show(): string | null {
      const refusal = ensureGhosts();
      if (refusal) {
        this.hide();
        return refusal;
      }
      on = true;
      for (const g of ghosts) g.mesh.visible = true;
      return null;
    },
    setOpacity(value: number): void {
      opacity = Math.min(1, Math.max(MIN_OPACITY, value));
      for (const g of ghosts) g.material.opacity = opacity;
    },
    hide(): void {
      on = false;
      for (const g of ghosts) g.mesh.visible = false;
    },
    dispose(): void {
      teardown();
      on = false;
    },
    probe() {
      return { on, opacity, ghosts: ghosts.length };
    },
  };
}
